import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
console.log("API_BASE_URL", API_BASE_URL)
const SignInLayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const emailInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Focus email input on mount
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
    
    // Check for redirect message from signup
    if (location.state?.fromSignUp) {
      toast.success('Registration successful! Please sign in.');
      // Clear the state to prevent showing the message again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to the first error
      const firstError = Object.keys(errors)[0];
      if (firstError) {
        document.querySelector(`[name="${firstError}"]`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }
    
    setIsLoading(true);
    
    try {
      
      const response = await axios.post(
        `${API_BASE_URL}/auth/login`,
        {
          email: formData.email.trim(),
          password: formData.password,
          remember_me: formData.rememberMe
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true
        }
      );

      console.log("LOGIN RESPONSE", response)
      // Use the AuthContext login function
      login({
        ...response.data,
        rememberMe: formData.rememberMe
      });
      
      toast.success('Successfully signed in!');
      
      // Redirect to the intended page or based on role (case-insensitive check)
      const userRole = response.data.role?.toUpperCase();
      const from = location.state?.from?.pathname || 
                 (response.data.role === 'ADMIN' ? '/admin-dashboard' : '/dashboard');
      navigate(from, { replace: true });
      
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'An error occurred during sign in';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 401) {
          errorMessage = 'Invalid email or password';
        } else if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'No response from server. Please try again later.';
      }
      
      toast.error(errorMessage);
      setErrors(prev => ({
        ...prev,
        form: errorMessage
      }));
    } finally {
      setIsLoading(false);
    }
  };
  // Toggle password visibility
  const togglePasswordVisibility = (inputId) => {
    const input = document.getElementById(inputId);
    if (input.type === 'password') {
      input.type = 'text';
    } else {
      input.type = 'password';
    }
  };

  return (
    <section className='auth bg-base d-flex flex-wrap'>
      <div className='auth-left d-lg-block d-none'>
        <div className='d-flex align-items-center flex-column h-100 justify-content-center'>
          <img src='assets/images/auth/auth-img.png' alt='' />
        </div>
      </div>
      <div className='auth-right py-32 px-24 d-flex flex-column justify-content-center'>
        <div className='max-w-464-px mx-auto w-100'>
          <div>
            <Link to='/' className='mb-40 max-w-290-px'>
              <img src='assets/images/logo.png' alt='' />
            </Link>
            <h4 className='mb-12'>Sign In to your Account</h4>
            <p className='mb-32 text-secondary-light text-lg'>
              Welcome back! Please enter your details
            </p>
          </div>
          
          {/* Display form-level errors */}
          {errors.form && (
            <div className='alert alert-danger' role='alert'>
              {errors.form}
            </div>
          )}
          
          <form onSubmit={handleSubmit} noValidate>
            <div className={`icon-field mb-16 ${errors.email ? 'has-error' : ''}`}>
              <span className='icon top-50 translate-middle-y'>
                <Icon icon='mage:email' />
              </span>
              <input
                ref={emailInputRef}
                type='email'
                name='email'
                className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.email ? 'is-invalid' : ''}`}
                placeholder='Email'
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
                autoComplete='username'
              />
              {errors.email && (
                <div className='invalid-feedback d-block'>{errors.email}</div>
              )}
            </div>
            
            <div className={`position-relative mb-20 ${errors.password ? 'has-error' : ''}`}>
              <div className='icon-field'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='solar:lock-password-outline' />
                </span>
                <input
                  type='password'
                  name='password'
                  id='password'
                  className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.password ? 'is-invalid' : ''}`}
                  placeholder='Password'
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  autoComplete='current-password'
                />
              </div>
              <span
                className='toggle-password ri-eye-line cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light'
                onClick={() => togglePasswordVisibility('password')}
                style={{ cursor: 'pointer' }}
              />
              {errors.password && (
                <div className='invalid-feedback d-block'>{errors.password}</div>
              )}
            </div>
            
            <div className='mb-24'>
              <div className='d-flex justify-content-between gap-2'>
                <div className='form-check style-check d-flex align-items-center'>
                  <input
                    className='form-check-input border border-neutral-300'
                    type='checkbox'
                    id='rememberMe'
                    name='rememberMe'
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <label className='form-check-label ms-2' htmlFor='rememberMe'>
                    Remember me
                  </label>
                </div>
                <Link 
                  to='/forgot-password' 
                  className='text-primary text-sm'
                  onClick={(e) => {
                    if (isLoading) e.preventDefault();
                  }}
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            <button 
              type='submit' 
              className='btn btn-primary w-100 h-56-px radius-12 d-flex align-items-center justify-content-center'
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
          <div className='text-center mt-32'>
            <p className='text-secondary-light mb-0'>
              Don't have an account?{' '}
              <Link 
                to='/sign-up' 
                className='text-primary text-decoration-underline'
                onClick={(e) => {
                  if (isLoading) e.preventDefault();
                }}
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignInLayer;